import { ProductTileResponse, ProductTileResponseSupplier, SupplierDeliveryWindow, SupplierPreference } from '@api/generated/search-service';
import { clientProductTileControllerApi } from '@api/api-config.ts';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import { guaranteedFirst, preferenceRank } from '@utils/priceVariants.ts';

/**
 * Modal detailu produktu se otevírá nad seznamem dlaždic (katalog, vyhledávání, nákupní seznam,
 * oblíbené). Změna uvnitř modalu platí i pro dlaždici pod ním — místo přenačtení celého seznamu
 * modal pošle, co se změnilo, a každý seznam si to promítne do svojí kopie produktu.
 *
 * Dvě podoby zprávy podle toho, jestli změnu umíme popsat, nebo si o ni musíme říct BE:
 * - `preference` nese data z odpovědi BE (označení dodavatelů) — bez dalšího requestu,
 * - `replaced` nese celou čerstvou dlaždici pro změny, které se z detailu spočítat nedají
 *   (přibyl dodavatel, a s ním i jiné ceny).
 */
export type ProductTileUpdate =
  | {
      kind: 'preference';
      productId: number;
      /** supplierId -> označení dodavatele; null = označení zrušeno. Dodavatel mimo mapu zůstane beze změny. */
      supplierPreferences: Record<number, SupplierPreference | null>;
      /** Produkt má aspoň jedno označení (i negativní) — štítek „Pref" na dlaždici. */
      hasSupplierPreference: boolean;
    }
  | {
      kind: 'replaced';
      productId: number;
      tile: ProductTileResponse;
      /** Doručovací okna dodavatelů dlaždice — bez nich by nově přidaný dodavatel neměl datum dodání. */
      supplierDelivery: SupplierDeliveryWindow[];
    };

const PRODUCT_TILE_UPDATE = 'product-tile-update';

const isValidNow = (supplier: ProductTileResponseSupplier): boolean => {
  const now = new Date();
  return (
    !!supplier.validPriceFrom && !!supplier.validPriceTo && new Date(supplier.validPriceFrom) <= now && new Date(supplier.validPriceTo) >= now
  );
};

/**
 * Stejné pořadí jako na BE (ProductTileReadService): platnost -> označení -> aukční cena
 * -> garantovaná varianta. Vyhledávání bere `suppliers[0]`, takže přeřadit se musí i tady.
 */
const bestSupplierFirst = (a: ProductTileResponseSupplier, b: ProductTileResponseSupplier): number => {
  const validDiff = (isValidNow(a) ? 0 : 1) - (isValidNow(b) ? 0 : 1);
  if (validDiff !== 0) return validDiff;

  const rankDiff = preferenceRank(a.supplierPreference) - preferenceRank(b.supplierPreference);
  if (rankDiff !== 0) return rankDiff;

  return a.auctionPrice - b.auctionPrice || guaranteedFirst(a, b);
};

/** Vrátí novou dlaždici se změnou, nebo tu původní (stejná reference = žádný re-render). */
export const applyProductTileUpdate = <T extends ProductTileResponse | null>(tile: T, update: ProductTileUpdate): T => {
  if (!tile || tile.productId !== update.productId) return tile;
  if (update.kind === 'replaced') return update.tile as T;

  const suppliers = tile.suppliers
    .map(supplier =>
      supplier.supplierId in update.supplierPreferences
        ? { ...supplier, supplierPreference: update.supplierPreferences[supplier.supplierId] ?? undefined }
        : supplier,
    )
    .sort(bestSupplierFirst);

  return { ...tile, suppliers, hasSupplierPreference: update.hasSupplierPreference };
};

export const applyProductTileUpdateToList = <T extends ProductTileResponse | null>(tiles: T[], update: ProductTileUpdate): T[] => {
  if (!tiles.some(tile => tile?.productId === update.productId)) return tiles;
  return tiles.map(tile => applyProductTileUpdate(tile, update));
};

/** Okna z aktualizace přepíšou stávající pro stejného dodavatele, zbytek seznamu zůstane. */
export const mergeSupplierDelivery = (
  current: SupplierDeliveryWindow[] | null,
  update: ProductTileUpdate,
): SupplierDeliveryWindow[] | null => {
  if (update.kind !== 'replaced' || !current) return current;

  const updatedSupplierIds = new Set(update.supplierDelivery.map(window => window.supplierId));
  return [...current.filter(window => !updatedSupplierIds.has(window.supplierId)), ...update.supplierDelivery];
};

export const notifyProductTileUpdate = (update: ProductTileUpdate) => {
  window.dispatchEvent(new CustomEvent<ProductTileUpdate>(PRODUCT_TILE_UPDATE, { detail: update }));
};

/**
 * Odběr se registruje na úrovni modulu storu, ne v komponentě — seznam tak zůstane aktuální,
 * i když zrovna není vykreslený (modal nad ním, jiná záložka).
 */
export const subscribeProductTileUpdate = (handler: (update: ProductTileUpdate) => void) => {
  window.addEventListener(PRODUCT_TILE_UPDATE, event => handler((event as CustomEvent<ProductTileUpdate>).detail));
};

/**
 * Dotáhne čerstvou dlaždici z BE a rozešle ji seznamům. Pro změny, které z detailu produktu
 * spočítat nejdou — čte se z databáze, takže vidí i to, co ještě neprošlo reindexem do ES.
 */
export const refreshProductTile = async (productId: number) => {
  const { orgId: organizationId, portalId } = useOrgPortal.getState();

  const response = await clientProductTileControllerApi.getProductTiles({ organizationId, portalId, productIds: [productId] });
  const tile = response.data.products.find(product => product.productId === productId);
  if (!tile) return;

  notifyProductTileUpdate({ kind: 'replaced', productId, tile, supplierDelivery: response.data.supplierDelivery });
};
